import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  TextField,
  Button,
  Container,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Grid,
  Box,
  useMediaQuery,
  useTheme,
  Typography,
  Divider,
  Card,
  CardContent,
  Autocomplete,
  CircularProgress,
  Checkbox,
  FormControlLabel,
  alpha,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import {
  QuotationCreate,
  QuotationUpdate,
  GenerateQuotationNo,
  ApprovePartyList,
  getVoltageRatio,
  getRatingSpecifications,
  getWindingMaterials,
  getAllEnquiries,
} from "../../../Services/Services";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

export default function FreightEntryForm() {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const location = useLocation();
  const navigate = useNavigate();

  // ── Theme-aware helpers ──
  const disabledBg = isDark
    ? alpha(theme.palette.common.white, 0.06)
    : "#f5f5f5";

  const grandTotalBg = isDark
    ? alpha(theme.palette.primary.main, 0.18)
    : "#e3f2fd";

  const tableHeadBg = isDark
    ? alpha(theme.palette.primary.main, 0.15)
    : "#f5f5f5";

  const termsPaperBg = isDark
    ? alpha(theme.palette.common.white, 0.04)
    : "#f9f9f9";

  const defaultTermBg = isDark
    ? alpha(theme.palette.common.white, 0.06)
    : "#f0f0f0";

  const rowBg = (index) =>
    index % 2 === 0
      ? isDark
        ? alpha(theme.palette.common.white, 0.03)
        : "#fafafa"
      : theme.palette.background.paper;

  // ---------- PARTY STATE ----------
  const [partyOptions, setPartyOptions] = useState([]);
  const [partyPage, setPartyPage] = useState(1);
  const [partySearch, setPartySearch] = useState("");
  const [partyLoading, setPartyLoading] = useState(false);
  const [selectedPartyObj, setSelectedPartyObj] = useState(null);

  // ---------- VOLTAGE / RATING / WINDING ----------
  const [voltageOptions, setVoltageOptions] = useState([]);
  const [voltageSearch, setVoltageSearch] = useState("");
  const [voltageLoading, setVoltageLoading] = useState(false);

  const [ratingOptions, setRatingOptions] = useState([]);
  const [ratingSearch, setRatingSearch] = useState("");
  const [ratingLoading, setRatingLoading] = useState(false);

  const [windingOptions, setWindingOptions] = useState([]);
  const [windingSearch, setWindingSearch] = useState("");
  const [windingLoading, setWindingLoading] = useState(false);

  // ---------- FORM STATE ----------
  const [loading, setLoading] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [errors, setErrors] = useState({});
  const [isFinalAgreedEditable, setIsFinalAgreedEditable] = useState(false);

  const [enquiryOptions, setEnquiryOptions] = useState([]);
  const [enquiryLoading, setEnquiryLoading] = useState(false);
  const [enquirySearch, setEnquirySearch] = useState("");

  const getSelectedDivision = () => {
    try {
      const stored = localStorage.getItem("selectedDivision");
      return stored ? JSON.parse(stored) : null;
    } catch (e) {
      console.error("Failed to parse selectedDivision:", e);
      return null;
    }
  };
  const storedDivision = getSelectedDivision();

  const [formData, setFormData] = useState({
    id: null,
    ref_no: "",
    date: "",
    to_name: "",
    to_title: "",
    to_company: "",
    to_address: "",
    contact_no: "",
    email: "",
    subject: "Transformer Quotation",
    body: "Dear sir, With reference to your email/enquiry , we are pleased to submit our quotation for the supply of transformer as per detail given below, for your kind consideration :SIDDHART Make Transformer of the under mentioned rating(s), 3 Phase, 50Hz, Core Type, double wound, ONAN cooled, HV/LV Connected in Delta/Star, Vector Group DYn11 with first filling of Transformer oil.",
    gst_percent: "",
    validity: "",
    Warranty: "",
    Force_Majeure: "",
    delivery: "",
    warranty: "",
    inspection_required: false,
    total_amount: 0,
    final_agreed_amount: 0,
    created_by: localStorage.getItem("user_id"),
    division: storedDivision?.division_id || "",
    party: "",
    contact_persons_list: [],
    terms_and_conditions: [
      { key: "GST", value: "@ 18%  ", is_default: true },
      { key: "Freight Charges", value: "", is_default: true },
      {
        key: "Delivery",
        value: "7 Weeks from the recepts of purchase order and advance.",
        is_default: true,
      },
      {
        key: "Payment",
        value: `a). 50% payment as advance with Purchase Order., \n          b). Balance along with 100% GST against proforma invoice prior to dispatch.`,
        is_default: true,
      },
      {
        key: "Validity",
        value: "3 days from the date of offer, subject to our confirmation.",
        is_default: true,
      },
      {
        key: "Warranty",
        value: `24 Months against any manufacturing defect from the date of dispatch., \n        In case of failure within warranty period you will have to send transformer to our works at your cost for repairing. \n        Failure due to oil theft, mishandling and improper installation will not be covered under warranty`,
        is_default: true,
      },
      {
        key: "Force Majeure",
        value:
          "The supplier shall not be liable for any delay or failure in execution due to circumstances beyond its control, including but not limited to acts of God, fire, floods, war, government restrictions, labor unrest, power shortages, transportation delays, or disruptions in supply of critical raw materials such as copper, CRGO steel, transformer oil, and other components. Delivery timelines shall be extended accordingly. In case such conditions continue beyond 30 days, the supplier reserves the right to revise delivery schedules, prices, or cancel the order without liability.",
        is_default: true,
      },
      {
        key: "Bank Details",
        value: `HDFC Bank,\n        Account No.:50200106700770,\n        IFSC Code: HDFC0006110`,
        is_default: true,
      },
      {
        key: "Unloading, Erection, Commissioning & Testing",
        value:
          "Unloading, Erection, Commissioning & Testing at site will not be in our scope. However if required, we shall provide supervisor at an extra cost of Rs 2500 per day plus to & fro fare, boarding and lodging and local conveyance.",
        is_default: true,
      },
      {
        key: "Interest & Storage cost towards Non Lifting of Transformer in Stipulated Time.",
        value:
          "We shall inform you the readiness of transformer in advance. In case you do not lift the transformer within 7 days, you will have to make the payment immediately failing which charge @ 24% p.a. as interest and storage charge from the date of intimation of readiness of transformer.",
        is_default: true,
      },
      {
        key: "Jurisdiction",
        value: "Subject to Raipur Jurisdiction.",
        is_default: true,
      },
      { key: "GST No", value: "22AELPC7589P1ZT", is_default: true },
      { key: "State Code", value: "22", is_default: true },
    ],
    items: [
      {
        voltage_ratio: "",
        rating_specification: "",
        winding_material: "",
        price_per_unit: 0,
      },
    ],
  });

  // ---------- API CALLS ----------

  const fetchNextQuotationNo = async () => {
    const data = await GenerateQuotationNo();
    if (data && data.ref_no) {
      setFormData((prev) => ({ ...prev, ref_no: data.ref_no }));
    }
  };

  const fetchEnquiries = async () => {
    try {
      setEnquiryLoading(true);
      const data = await getAllEnquiries();
      // Django API se array milega, usko options state me set kar rahe hain
      setEnquiryOptions(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Error fetching enquiries:", err);
    } finally {
      setEnquiryLoading(false);
    }
  };

  // Component load hone par enquiries fetch karein
  useEffect(() => {
    fetchEnquiries();
  }, []);

  const getParties = async (pageNum = 1, filters = {}) => {
    try {
      setPartyLoading(true);
      const response = await ApprovePartyList(pageNum, filters);
      const results = Array.isArray(response) ? response : [];
      let finalOptions = results;
      if (
        selectedPartyObj &&
        !finalOptions.some((p) => p.id === selectedPartyObj.id)
      ) {
        finalOptions = [selectedPartyObj, ...finalOptions];
      }
      if (pageNum === 1) {
        setPartyOptions(finalOptions);
      } else {
        setPartyOptions((prev) => {
          const merged = [...prev, ...finalOptions];
          if (
            selectedPartyObj &&
            !merged.some((p) => p.id === selectedPartyObj.id)
          ) {
            return [selectedPartyObj, ...merged];
          }
          return merged;
        });
      }
      setPartyPage(pageNum);
    } catch (err) {
      console.error("Error fetching parties:", err);
      toast.error("Failed to load parties");
    } finally {
      setPartyLoading(false);
    }
  };

  const fetchVoltageOptions = async (search = "") => {
    try {
      setVoltageLoading(true);
      const data = await getVoltageRatio(1, search);
      setVoltageOptions(data.results || []);
    } catch (err) {
      console.error("Error fetching voltage ratios:", err);
    } finally {
      setVoltageLoading(false);
    }
  };

  const fetchRatingOptions = async (search = "") => {
    try {
      setRatingLoading(true);
      const data = await getRatingSpecifications(1, search);
      setRatingOptions(data.results || []);
    } catch (err) {
      console.error("Error fetching rating specs:", err);
    } finally {
      setRatingLoading(false);
    }
  };

  const fetchWindingOptions = async (search = "") => {
    try {
      setWindingLoading(true);
      const data = await getWindingMaterials(1, search);
      setWindingOptions(data.results || []);
    } catch (err) {
      console.error("Error fetching winding materials:", err);
    } finally {
      setWindingLoading(false);
    }
  };

  // ---------- EFFECTS ----------
  useEffect(() => {
    const quoteData = location.state?.data ?? null;
    if (!quoteData && !isEditMode) fetchNextQuotationNo();
    getParties(1, { name: "" });
    fetchVoltageOptions("");
    fetchRatingOptions("");
    fetchWindingOptions("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditMode]);

  useEffect(() => {
    const timer = setTimeout(() => {
      getParties(1, { name: partySearch });
    }, 500);
    return () => clearTimeout(timer);
  }, [partySearch]);

  useEffect(() => {
    const t = setTimeout(() => fetchVoltageOptions(voltageSearch), 400);
    return () => clearTimeout(t);
  }, [voltageSearch]);

  useEffect(() => {
    const t = setTimeout(() => fetchRatingOptions(ratingSearch), 400);
    return () => clearTimeout(t);
  }, [ratingSearch]);

  useEffect(() => {
    const t = setTimeout(() => fetchWindingOptions(windingSearch), 400);
    return () => clearTimeout(t);
  }, [windingSearch]);

  const quoteData = location.state?.data ?? null;
  useEffect(() => {
    if (!quoteData) return;
    setIsEditMode(true);

    const normalizedItems =
      Array.isArray(quoteData.items_list) && quoteData.items_list.length > 0
        ? quoteData.items_list.map((item) => ({
            voltage_ratio:
              item.voltage_ratio ?? item.voltageRatio ?? item.description ?? "",
            rating_specification:
              item.rating_specification ??
              item.ratingSpecification ??
              item.rating ??
              item.specification ??
              "",
            winding_material:
              item.winding_material ??
              item.windingMaterial ??
              item.unit ??
              item.uom ??
              "",
            price_per_unit:
              parseFloat(
                item.price_per_unit ??
                  item.pricePerUnit ??
                  item.rate ??
                  item.amount,
              ) || 0,
          }))
        : [
            {
              voltage_ratio: "",
              rating_specification: "",
              winding_material: "",
              price_per_unit: 0,
            },
          ];

    const matchedParty = partyOptions.find(
      (p) => p.company_name === quoteData.to_company,
    );

    const updatedData = {
      id: quoteData.quotation_id,
      ref_no: quoteData.ref_no,
      date: quoteData.date ? quoteData.date : "",
      to_name: quoteData.to_name || "",
      to_title: quoteData.to_title || "",
      to_company: quoteData.to_company || "",
      to_address: quoteData.to_address || "",
      contact_no: quoteData.contact_no || "",
      email: quoteData.email || "",
      subject: quoteData.subject || "",
      body: quoteData.body || "",
      gst_percent: quoteData.gst_percent || "",
      validity: quoteData.validity || "",
      pf: quoteData.pf || "",
      transport_insurance: quoteData.transport_insurance || "",
      delivery: quoteData.delivery || "",
      warranty: quoteData.warranty || "",
      inspection_required: quoteData.inspection_required || false,
      total_amount: quoteData.total_amount || 0,
      final_agreed_amount: quoteData.final_agreed_amount || 0,
      scope_of_work: quoteData.scope_of_work || [],
      exclusions: quoteData.exclusions || [],
      terms_and_conditions: quoteData.terms_and_conditions || [],
      division: storedDivision?.division_id || "",
      party: matchedParty ? matchedParty.id : "",
      contact_persons_list: matchedParty?.contact_persons || [],
      items: normalizedItems,
    };

    if (
      quoteData.final_agreed_amount &&
      parseFloat(quoteData.final_agreed_amount) > 0
    ) {
      setIsFinalAgreedEditable(true);
    }

    const recalculatedTotal = normalizedItems.reduce(
      (sum, it) => sum + (Number(it.price_per_unit) || 0),
      0,
    );
    updatedData.total_amount = recalculatedTotal;
    setFormData(updatedData);
  }, [quoteData, partyOptions]);

  useEffect(() => {
    const ensureOptions = (items, options, key, setter) => {
      const missing = items
        .map((i) => i[key])
        .filter(Boolean)
        .filter((name) => !options.some((o) => o.name === name))
        .map((name) => ({ id: `temp-${name}`, name }));
      if (missing.length) setter((prev) => [...missing, ...prev]);
    };
    if (
      formData.items.length &&
      (voltageOptions.length || ratingOptions.length || windingOptions.length)
    ) {
      ensureOptions(
        formData.items,
        voltageOptions,
        "voltage_ratio",
        setVoltageOptions,
      );
      ensureOptions(
        formData.items,
        ratingOptions,
        "rating_specification",
        setRatingOptions,
      );
      ensureOptions(
        formData.items,
        windingOptions,
        "winding_material",
        setWindingOptions,
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.items]);

  // ---------- HANDLERS ----------
  const handleChange = (e) => {
    const { name, value } = e.target;
    let error = "";
    let updatedValue = value;

    if (name === "contact_no") {
      updatedValue = value.replace(/\D/g, "");
      if (updatedValue.length > 10) updatedValue = updatedValue.slice(0, 10);
      if (updatedValue.length !== 10 && updatedValue.length > 0)
        error = "Must be exactly 10 digits";
    }

    if (name === "email") {
      updatedValue = updatedValue.trim();
      if (updatedValue && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(updatedValue))
        error = "Invalid email format";
    }

    if (name === "final_agreed_amount") {
      setFormData((prev) => ({
        ...prev,
        final_agreed_amount: parseFloat(value) || 0,
      }));
      return;
    }

    setFormData((prev) => ({ ...prev, [name]: updatedValue }));
    setErrors((prev) => ({ ...prev, [name]: error }));
  };

  const handleFinalAgreedToggle = (e) => {
    const checked = e.target.checked;
    setIsFinalAgreedEditable(checked);
    if (!checked) setFormData((prev) => ({ ...prev, final_agreed_amount: 0 }));
  };

  const handleQuotationSubmit = async (e) => {
    e.preventDefault();
    const userId = localStorage.getItem("user_id");
    if (!userId) {
      alert("User not logged in. Please login again.");
      return;
    }
    setLoading(true);
    try {
      const cleanedItems = formData.items.map((item) => ({
        voltage_ratio: item.voltage_ratio,
        rating_specification: item.rating_specification,
        winding_material: item.winding_material,
        price_per_unit: Number(item.price_per_unit) || 0,
        amount: Number(item.price_per_unit) || 0,
        description: item.voltage_ratio,
        unit: item.winding_material,
        quantity: 1,
        rate: Number(item.price_per_unit) || 0,
        uom: item.winding_material,
      }));

      const payload = {
        ...formData,
        division: null, // ✅ FIX: Invalid PK error rokne ke liye null bhejein
        created_by: parseInt(formData.created_by || userId, 10),
        items: cleanedItems,
        to_title: formData.to_title || "",
        final_agreed_amount: formData.final_agreed_amount || 0,
        total_amount: formData.total_amount || 0,
        date:
          formData.date && formData.date.trim() !== "" ? formData.date : null,
      };

      if (isEditMode) {
        await QuotationUpdate(payload.id, payload);
        toast.success("Quotation updated successfully!", {
          onClose: () => navigate("/dashboard/quotation"),
          autoClose: 2000,
        });
      } else {
        await QuotationCreate(payload);
        toast.success("Quotation created successfully!", {
          onClose: () => navigate("/dashboard/quotation"),
          autoClose: 2000,
        });
      }
    } catch (error) {
      console.error("Submission Error Details:", error);

      // 🔍 Backend Validation Error display logic
      const errorData = error.response?.data;
      if (errorData && typeof errorData === "object") {
        const errorMessages = Object.entries(errorData)
          .map(([field, msgs]) => `${field}: ${Array.isArray(msgs) ? msgs.join(", ") : msgs}`)
          .join(" | ");
        toast.error(`Backend Error: ${errorMessages}`);
      } else {
        toast.error("Something went wrong! Failed to save quotation.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleEnquirySelect = (event, selectedEnquiry) => {
    if (!selectedEnquiry) return;

    // 1. Party options me company search karein
    const matchedParty = partyOptions.find(
      (p) =>
        p.company_name?.toLowerCase() ===
        selectedEnquiry.company_name?.toLowerCase(),
    );

    // 2. Full Address banayein
    const fullAddress = [
      selectedEnquiry.address,
      selectedEnquiry.city,
      selectedEnquiry.state,
    ]
      .filter(Boolean)
      .join(", ");

    // 3. Line Items mapping (Enquiry line_items -> Quotation items)
    const mappedItems =
      Array.isArray(selectedEnquiry.line_items) &&
      selectedEnquiry.line_items.length > 0
        ? selectedEnquiry.line_items.map((item) => ({
            voltage_ratio: item.voltage_ratio || "",
            rating_specification:
              item.rating_specification || item.description || "",
            winding_material: item.winding_material || "",
            price_per_unit: parseFloat(item.price_per_unit) || 0,
          }))
        : [
            {
              voltage_ratio: "",
              rating_specification: "",
              winding_material: "",
              price_per_unit: 0,
            },
          ];

    // Total Amount Calculate karein
    const calculatedTotal = mappedItems.reduce(
      (sum, item) => sum + (item.price_per_unit || 0),
      0,
    );

    // 4. Quotation Form me Auto-fill karein
    setFormData((prev) => ({
      ...prev,
      party: matchedParty ? matchedParty.id : "",
      to_company: selectedEnquiry.company_name || "",
      to_name: selectedEnquiry.customer_name || "",
      contact_no: selectedEnquiry.phone || "",
      email: selectedEnquiry.email || "",
      to_address: fullAddress || selectedEnquiry.address || "",
      subject: selectedEnquiry.enq_type
        ? `Quotation for ${selectedEnquiry.enq_type}`
        : "Transformer Quotation",
      items: mappedItems,
      total_amount: calculatedTotal,
      contact_persons_list: matchedParty?.contact_persons || [],
    }));

    if (matchedParty) {
      setSelectedPartyObj(matchedParty);
    }
  };

  const handleItemChange = (e, index) => {
    const { name, value } = e.target;
    const updatedItems = [...formData.items];

    if (name === "price_per_unit") {
      if (value !== "" && !/^\d*\.?\d*$/.test(value)) return;
      if (value === "") {
        updatedItems[index][name] = "";
      } else if (
        typeof value === "string" &&
        value.endsWith(".") &&
        /^\d+\.$/.test(value)
      ) {
        updatedItems[index][name] = value;
      } else {
        const numericValue = parseFloat(value);
        updatedItems[index][name] = isNaN(numericValue) ? "" : numericValue;
      }
      const totalAmount = updatedItems.reduce(
        (sum, item) => sum + (parseFloat(item.price_per_unit) || 0),
        0,
      );
      setFormData((prev) => ({
        ...prev,
        items: updatedItems,
        total_amount: totalAmount,
      }));
      return;
    }

    updatedItems[index][name] = value;
    setFormData((prev) => ({ ...prev, items: updatedItems }));
  };

  const handleAutocompleteChange = (index, field, newValue) => {
    const updatedItems = [...formData.items];
    updatedItems[index][field] = newValue ? newValue.name : "";
    setFormData((prev) => ({ ...prev, items: updatedItems }));
  };

  const handlePriceBlur = (index) => {
    const updatedItems = [...formData.items];
    const val = updatedItems[index].price_per_unit;
    if (typeof val === "string") {
      const num = parseFloat(val) || 0;
      updatedItems[index].price_per_unit = num;
      const totalAmount = updatedItems.reduce(
        (sum, item) => sum + (parseFloat(item.price_per_unit) || 0),
        0,
      );
      setFormData((prev) => ({
        ...prev,
        items: updatedItems,
        total_amount: totalAmount,
      }));
    }
  };

  const addItem = () => {
    setFormData((prev) => ({
      ...prev,
      items: [
        ...prev.items,
        {
          voltage_ratio: "",
          rating_specification: "",
          winding_material: "",
          price_per_unit: 0,
        },
      ],
    }));
  };

  const deleteItem = (indexToDelete) => {
    const updatedItems = formData.items.filter((_, i) => i !== indexToDelete);
    const finalItems =
      updatedItems.length === 0
        ? [
            {
              voltage_ratio: "",
              rating_specification: "",
              winding_material: "",
              price_per_unit: 0,
            },
          ]
        : updatedItems;
    const totalAmount = finalItems.reduce(
      (sum, item) => sum + (parseFloat(item.price_per_unit) || 0),
      0,
    );
    setFormData((prev) => ({
      ...prev,
      items: finalItems,
      total_amount: totalAmount,
    }));
  };

  const handleTermChange = (index, field, value) => {
    setFormData((prev) => {
      const updatedTerms = [...prev.terms_and_conditions];
      updatedTerms[index][field] = value;
      return { ...prev, terms_and_conditions: updatedTerms };
    });
  };

  const addTerm = () => {
    setFormData((prev) => ({
      ...prev,
      terms_and_conditions: [
        ...prev.terms_and_conditions,
        { key: "", value: "", is_default: false },
      ],
    }));
  };

  const removeTerm = (index) => {
    setFormData((prev) => ({
      ...prev,
      terms_and_conditions: prev.terms_and_conditions.filter(
        (_, i) => i !== index,
      ),
    }));
  };

  // ── Shared autocomplete field (DRY helper) ──
  const renderMasterAutocomplete = ({
    options,
    loading: isLoading,
    value,
    onChange,
    onInputChange,
    label,
    placeholder,
    size = "small",
  }) => (
    <Autocomplete
      options={options}
      getOptionLabel={(option) => option.name || ""}
      filterOptions={(x) => x}
      value={value}
      onChange={onChange}
      onInputChange={onInputChange}
      loading={isLoading}
      size={size}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          placeholder={placeholder}
          size={size}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {isLoading ? <CircularProgress size={16} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
      noOptionsText={isLoading ? "Loading..." : `No ${label} found`}
      isOptionEqualToValue={(opt, val) => opt.name === val.name}
    />
  );

  return (
    <Container maxWidth="xl" sx={{ px: { xs: 2, sm: 2 }, py: 2 }}>
      <ToastContainer
        position="top-right"
        autoClose={3000}
        theme={isDark ? "dark" : "light"}
      />

      <form onSubmit={handleQuotationSubmit} autoComplete="off">
        {/* ── Top Action Bar ── */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            gap: 2,
            mb: 2,
            mt: { xs: 2, md: -3 },
            position: "sticky",
            top: 0,
            zIndex: 1100,
            pt: 1,
            pb: 1,
            borderBottom: "1px solid",
            borderColor: "divider",
            bgcolor: (t) => alpha(t.palette.background.paper, 0.95),
            backdropFilter: "blur(8px)",
            boxShadow: isDark
              ? "0 2px 10px rgba(0,0,0,0.45)"
              : "0 1px 4px rgba(0,0,0,0.07)",
          }}
        >
          <Button
            type="submit"
            variant="contained"
            color="primary"
            disabled={loading}
            fullWidth={isMobile}
            sx={{ height: { xs: 40, md: 36 }, borderRadius: 0 }}
          >
            {loading ? (
              <CircularProgress size={20} color="inherit" />
            ) : isEditMode ? (
              "Update"
            ) : (
              "Submit"
            )}
          </Button>
        </Box>

        {/* ── Header Form Box ── */}
        {/* ── Header Form Box ── */}
        <Box
          sx={{
            border: "1px solid",
            borderColor: "divider",
            borderRadius: "8px",
            mb: 2,
            p: 2,
            bgcolor: "background.paper",
            backgroundImage: "none",
            boxShadow: isDark ? "0 4px 18px rgba(0,0,0,0.35)" : "none",
          }}
        >
          <Grid container spacing={2}>
            {/* --- ROW 1 --- */}
            {/* Quotation Ref No */}
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                fullWidth
                size={isMobile ? "small" : "medium"}
                label="Quotation Ref No"
                name="ref_no"
                value={formData.ref_no}
                onChange={handleChange}
                required
                disabled
                sx={{ "& .MuiInputBase-root": { bgcolor: disabledBg } }}
              />
            </Grid>

            {/* Date */}
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                fullWidth
                size={isMobile ? "small" : "medium"}
                required
                label="Date"
                name="date"
                type="date"
                value={formData.date}
                onChange={handleChange}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            {/* Import From Enquiry Dropdown */}
            <Grid item xs={12} sm={12} md={4}>
              <Autocomplete
                options={enquiryOptions}
                getOptionLabel={(option) =>
                  `${option.company_name || option.customer_name || "N/A"} - ${
                    option.enquiry_source || option.enq_type || "Enquiry"
                  }`
                }
                onChange={handleEnquirySelect}
                loading={enquiryLoading}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Import from Enquiry (Optional)"
                    placeholder="Search Enquiry..."
                    size={isMobile ? "small" : "medium"}
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {enquiryLoading && (
                            <CircularProgress color="inherit" size={20} />
                          )}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        bgcolor: isDark
                          ? alpha(theme.palette.primary.main, 0.1)
                          : "#f0f7ff",
                      },
                    }}
                  />
                )}
              />
            </Grid>

            {/* --- ROW 2 --- */}
            {/* Company Name */}
            <Grid item xs={12} sm={6} md={4}>
              <Autocomplete
                options={partyOptions}
                getOptionLabel={(option) => option.company_name || ""}
                filterOptions={(options) => options}
                value={
                  partyOptions.find((option) => option.id === formData.party) ||
                  null
                }
                onChange={(event, newValue) => {
                  if (newValue) {
                    const contactPerson = newValue.contact_persons?.[0] || {};
                    setFormData((prev) => ({
                      ...prev,
                      party: newValue.id,
                      to_company: newValue.company_name || "",
                      to_name: contactPerson.contact_name || "",
                      to_address: newValue.address || "",
                      contact_no:
                        newValue.contact || contactPerson.contact_no || "",
                      email: newValue.email || "",
                      contact_persons_list: newValue.contact_persons || [],
                    }));
                    setSelectedPartyObj(newValue);
                  } else {
                    setFormData((prev) => ({
                      ...prev,
                      party: "",
                      to_company: "",
                      to_name: "",
                      to_address: "",
                      contact_no: "",
                      email: "",
                      contact_persons_list: [],
                    }));
                    setSelectedPartyObj(null);
                  }
                }}
                onInputChange={(event, newInputValue, reason) => {
                  if (reason === "input") setPartySearch(newInputValue);
                }}
                renderOption={(props, option) => (
                  <li {...props}>
                    <Box>
                      <Typography variant="body1">
                        {option.company_name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {option.name} {option.contact && `| ${option.contact}`}
                      </Typography>
                    </Box>
                  </li>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Company Name"
                    required
                    size={isMobile ? "small" : "medium"}
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {partyLoading && (
                            <CircularProgress color="inherit" size={20} />
                          )}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
            </Grid>

            {/* To Name */}
            <Grid item xs={12} sm={6} md={4}>
              <Autocomplete
                options={formData.contact_persons_list || []}
                getOptionLabel={(option) =>
                  option.title
                    ? `${option.title} ${option.contact_name}`
                    : option.contact_name || ""
                }
                value={
                  formData.contact_persons_list?.find((p) => {
                    const fullName = p.title
                      ? `${p.title} ${p.contact_name}`
                      : p.contact_name;
                    return fullName === formData.to_name;
                  }) || null
                }
                onChange={(event, newValue) => {
                  if (newValue) {
                    setFormData((prev) => ({
                      ...prev,
                      to_name: newValue.title
                        ? `${newValue.title} ${newValue.contact_name}`
                        : newValue.contact_name,
                      contact_no: newValue.contact_no || prev.contact_no,
                      to_title: newValue.title || "",
                    }));
                  }
                }}
                renderOption={(props, option) => (
                  <li {...props}>
                    <Box>
                      <Typography variant="body2">
                        {option.title ? `${option.title} ` : ""}
                        {option.contact_name}
                      </Typography>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        display="block"
                      >
                        {option.designation || "No designation"}
                        {option.contact_no && ` | ${option.contact_no}`}
                      </Typography>
                    </Box>
                  </li>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="To Name"
                    required
                    size={isMobile ? "small" : "medium"}
                    disabled={!formData.party}
                    helperText={!formData.party ? "Select a debtor first" : ""}
                  />
                )}
                isOptionEqualToValue={(option, value) => {
                  const optName = option.title
                    ? `${option.title} ${option.contact_name}`
                    : option.contact_name;
                  const valName = value?.title
                    ? `${value.title} ${value.contact_name}`
                    : value?.contact_name;
                  return optName === valName;
                }}
              />
            </Grid>

            {/* Contact No */}
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                fullWidth
                size={isMobile ? "small" : "medium"}
                label="Contact No."
                name="contact_no"
                value={formData.contact_no}
                onChange={handleChange}
                error={Boolean(errors.contact_no)}
                helperText={errors.contact_no}
                required
              />
            </Grid>

            {/* --- ROW 3 --- */}
            {/* To Address (अब आधा चौड़ा) */}
            <Grid item xs={12} sm={6} md={6}>
              <TextField
                fullWidth
                size={isMobile ? "small" : "medium"}
                label="To Address"
                name="to_address"
                value={formData.to_address}
                onChange={handleChange}
                required
              />
            </Grid>

            {/* Email (Address के बगल में) */}
            <Grid item xs={12} sm={6} md={6}>
              <TextField
                fullWidth
                size={isMobile ? "small" : "medium"}
                label="Email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                error={Boolean(errors.email)}
                helperText={errors.email}
               
              />
            </Grid>

            {/* --- ROW 4 --- */}
            {/* Subject */}
            <Grid item xs={12} sm={12} md={12}>
              <TextField
                fullWidth
                size={isMobile ? "small" : "medium"}
                label="Subject"
                name="subject"
                value={formData.subject}
                onChange={handleChange}
                required
              />
            </Grid>

            {/* --- ROW 5 --- */}
            {/* Body */}
            <Grid item xs={12} sm={12} md={12}>
              <TextField
                fullWidth
                size={isMobile ? "small" : "medium"}
                label="Body"
                name="body"
                value={formData.body}
                onChange={handleChange}
                multiline
                rows={isMobile ? 1 : 0}
              />
            </Grid>
          </Grid>
        </Box>

        {/* ── ITEMS SECTION ── */}
        {isMobile ? (
          <Box sx={{ mb: 2 }}>
            {formData.items.map((item, index) => (
              <Card
                key={index}
                elevation={0}
                sx={{
                  mb: 2,
                  border: "1px solid",
                  borderColor: "divider",
                  bgcolor: "background.paper",
                  backgroundImage: "none",
                  boxShadow: isDark ? "0 2px 10px rgba(0,0,0,0.35)" : 2,
                }}
              >
                <CardContent>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      mb: 1,
                    }}
                  >
                    <Typography
                      variant="subtitle2"
                      fontWeight="bold"
                      sx={{ color: "text.primary" }}
                    >
                      Item #{index + 1}
                    </Typography>
                    <IconButton
                      color="error"
                      size="small"
                      onClick={() => deleteItem(index)}
                      sx={{
                        "&:hover": {
                          bgcolor: (t) =>
                            t.palette.mode === "dark"
                              ? alpha(t.palette.error.main, 0.15)
                              : "#ffebee",
                        },
                      }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>

                  <Divider sx={{ mb: 2, borderColor: "divider" }} />

                  <Grid container spacing={1.5}>
                    {/* Voltage Ratio */}
                    <Grid item xs={12}>
                      {renderMasterAutocomplete({
                        options: voltageOptions,
                        loading: voltageLoading,
                        value:
                          voltageOptions.find(
                            (o) => o.name === item.voltage_ratio,
                          ) ||
                          (item.voltage_ratio
                            ? { name: item.voltage_ratio }
                            : null),
                        onChange: (e, newValue) =>
                          handleAutocompleteChange(
                            index,
                            "voltage_ratio",
                            newValue,
                          ),
                        onInputChange: (e, val, reason) => {
                          if (reason === "input") setVoltageSearch(val);
                        },
                        label: "Voltage Ratio",
                        placeholder: "Search e.g. 11000/433",
                      })}
                    </Grid>

                    {/* Rating Specification */}
                    <Grid item xs={12}>
                      {renderMasterAutocomplete({
                        options: ratingOptions,
                        loading: ratingLoading,
                        value:
                          ratingOptions.find(
                            (o) => o.name === item.rating_specification,
                          ) ||
                          (item.rating_specification
                            ? { name: item.rating_specification }
                            : null),
                        onChange: (e, newValue) =>
                          handleAutocompleteChange(
                            index,
                            "rating_specification",
                            newValue,
                          ),
                        onInputChange: (e, val, reason) => {
                          if (reason === "input") setRatingSearch(val);
                        },
                        label: "Rating Specification",
                        placeholder: "Search e.g. 500 KVA",
                      })}
                    </Grid>

                    {/* Winding Material */}
                    <Grid item xs={12}>
                      {renderMasterAutocomplete({
                        options: windingOptions,
                        loading: windingLoading,
                        value:
                          windingOptions.find(
                            (o) => o.name === item.winding_material,
                          ) ||
                          (item.winding_material
                            ? { name: item.winding_material }
                            : null),
                        onChange: (e, newValue) =>
                          handleAutocompleteChange(
                            index,
                            "winding_material",
                            newValue,
                          ),
                        onInputChange: (e, val, reason) => {
                          if (reason === "input") setWindingSearch(val);
                        },
                        label: "Winding Material",
                        placeholder: "Search e.g. Copper",
                      })}
                    </Grid>

                    {/* Price */}
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Price Per Unit in Rs"
                        name="price_per_unit"
                        type="number"
                        value={item.price_per_unit}
                        onChange={(e) => handleItemChange(e, index)}
                        onBlur={() => handlePriceBlur(index)}
                        placeholder="e.g. 250000"
                        inputProps={{ inputMode: "numeric", pattern: "[0-9]*" }}
                        onKeyDown={(e) => {
                          if (["e", "E", "+", "-"].includes(e.key))
                            e.preventDefault();
                        }}
                        InputProps={{
                          startAdornment: (
                            <Typography sx={{ mr: 1, color: "text.primary" }}>
                              ₹
                            </Typography>
                          ),
                        }}
                      />
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            ))}

            <Button
              onClick={addItem}
              variant="outlined"
              fullWidth
              startIcon={<AddIcon />}
              sx={{ mb: 2, borderRadius: 0 }}
            >
              Add Item
            </Button>

            {/* Mobile Grand Total */}
            <Box
              sx={{
                bgcolor: grandTotalBg,
                p: 2,
                borderRadius: 1,
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                flexWrap: "wrap",
                gap: 1,
                border: "2px solid",
                borderColor: "primary.main",
              }}
            >
              <Typography
                variant="subtitle1"
                fontWeight="bold"
                sx={{ color: "text.primary" }}
              >
                Grand Total
              </Typography>
              <Typography variant="h6" color="primary" fontWeight="bold">
                ₹ {Number(formData.total_amount || 0).toFixed(2)}
              </Typography>
            </Box>

            {/* Mobile Final Agreed */}
            <Box
              sx={{
                bgcolor: (t) =>
                  t.palette.mode === "dark"
                    ? alpha(t.palette.common.white, 0.04)
                    : "#f5f5f5",
                p: 2,
                borderRadius: 1,
                display: "flex",
                flexDirection: isMobile ? "column" : "row",
                alignItems: isMobile ? "stretch" : "center",
                gap: 1,
                mt: 2,
                border: "1px solid",
                borderColor: "divider",
              }}
            >
              <FormControlLabel
                control={
                  <Checkbox
                    checked={isFinalAgreedEditable}
                    onChange={handleFinalAgreedToggle}
                    color="primary"
                  />
                }
                label="Enable Final Agreed Amount"
                sx={{ color: "text.primary" }}
              />
              <TextField
                fullWidth
                size="small"
                label="Final Agreed Amount"
                name="final_agreed_amount"
                type="number"
                value={formData.final_agreed_amount || 0}
                onChange={handleChange}
                disabled={!isFinalAgreedEditable}
                sx={{
                  "& .MuiInputBase-root": {
                    bgcolor: !isFinalAgreedEditable ? disabledBg : undefined,
                  },
                }}
                InputProps={{
                  startAdornment: (
                    <Typography sx={{ mr: 1, color: "text.primary" }}>
                      ₹
                    </Typography>
                  ),
                }}
              />
            </Box>
          </Box>
        ) : (
          <>
            {/* ── DESKTOP TABLE ── */}
            <TableContainer
              component={Paper}
              elevation={0}
              sx={{
                position: "relative",
                mb: 2,
                overflowX: "auto",
                border: "1px solid",
                borderColor: "divider",
                bgcolor: "background.paper",
                backgroundImage: "none",
                boxShadow: isDark ? "0 4px 18px rgba(0,0,0,0.35)" : "none",
              }}
            >
              <Table size="small" sx={{ tableLayout: "fixed", width: "100%" }}>
                <TableHead
                  sx={{
                    "& th": {
                      fontWeight: "bold",
                      color: "text.primary",
                      backgroundColor: `${tableHeadBg} !important`,
                      borderBottom: "2px solid",
                      borderColor: "primary.main",
                    },
                  }}
                >
                  <TableRow>
                    <TableCell sx={{ width: "28%" }}>Voltage Ratio</TableCell>
                    <TableCell sx={{ width: "27%" }}>
                      Rating Specification
                    </TableCell>
                    <TableCell sx={{ width: "22%" }}>
                      Winding Material
                    </TableCell>
                    <TableCell sx={{ width: "18%" }}>
                      Price Per Unit in Rs
                    </TableCell>
                    <TableCell sx={{ width: "5%" }} align="center">
                      Action
                    </TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {formData.items.map((item, index) => (
                    <TableRow
                      key={index}
                      sx={{
                        bgcolor: rowBg(index),
                        "& td": {
                          borderBottom: "1px solid",
                          borderColor: "divider",
                          color: "text.primary",
                        },
                      }}
                    >
                      {/* Voltage */}
                      <TableCell>
                        {renderMasterAutocomplete({
                          options: voltageOptions,
                          loading: voltageLoading,
                          value:
                            voltageOptions.find(
                              (o) => o.name === item.voltage_ratio,
                            ) ||
                            (item.voltage_ratio
                              ? { name: item.voltage_ratio }
                              : null),
                          onChange: (e, newValue) =>
                            handleAutocompleteChange(
                              index,
                              "voltage_ratio",
                              newValue,
                            ),
                          onInputChange: (e, val, reason) => {
                            if (reason === "input") setVoltageSearch(val);
                          },
                          label: "",
                          placeholder: "Search",
                        })}
                      </TableCell>

                      {/* Rating */}
                      <TableCell>
                        {renderMasterAutocomplete({
                          options: ratingOptions,
                          loading: ratingLoading,
                          value:
                            ratingOptions.find(
                              (o) => o.name === item.rating_specification,
                            ) ||
                            (item.rating_specification
                              ? { name: item.rating_specification }
                              : null),
                          onChange: (e, newValue) =>
                            handleAutocompleteChange(
                              index,
                              "rating_specification",
                              newValue,
                            ),
                          onInputChange: (e, val, reason) => {
                            if (reason === "input") setRatingSearch(val);
                          },
                          label: "",
                          placeholder: "Search",
                        })}
                      </TableCell>

                      {/* Winding */}
                      <TableCell>
                        {renderMasterAutocomplete({
                          options: windingOptions,
                          loading: windingLoading,
                          value:
                            windingOptions.find(
                              (o) => o.name === item.winding_material,
                            ) ||
                            (item.winding_material
                              ? { name: item.winding_material }
                              : null),
                          onChange: (e, newValue) =>
                            handleAutocompleteChange(
                              index,
                              "winding_material",
                              newValue,
                            ),
                          onInputChange: (e, val, reason) => {
                            if (reason === "input") setWindingSearch(val);
                          },
                          label: "",
                          placeholder: "Search",
                        })}
                      </TableCell>

                      {/* Price */}
                      <TableCell>
                        <TextField
                          fullWidth
                          size="small"
                          name="price_per_unit"
                          type="number"
                          value={item.price_per_unit ?? ""}
                          onChange={(e) => handleItemChange(e, index)}
                          onBlur={() => handlePriceBlur(index)}
                          placeholder="0"
                          inputProps={{
                            inputMode: "numeric",
                            pattern: "[0-9]*",
                            min: 0,
                          }}
                          onKeyDown={(e) => {
                            if (["e", "E", "+", "-"].includes(e.key))
                              e.preventDefault();
                          }}
                          InputProps={{
                            startAdornment: (
                              <Typography
                                sx={{ mr: 0.5, color: "text.primary" }}
                              >
                                ₹
                              </Typography>
                            ),
                          }}
                        />
                      </TableCell>

                      {/* Delete */}
                      <TableCell align="center">
                        <IconButton
                          color="error"
                          onClick={() => deleteItem(index)}
                          size="small"
                          sx={{
                            "&:hover": {
                              bgcolor: (t) =>
                                t.palette.mode === "dark"
                                  ? alpha(t.palette.error.main, 0.15)
                                  : "#ffebee",
                            },
                          }}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}

                  {/* Grand Total Row */}
                  <TableRow
                    sx={{
                      position: "sticky",
                      bottom: 56,
                      zIndex: 3,
                      "& td": { bgcolor: "background.paper" },
                    }}
                  >
                    <TableCell
                      colSpan={3}
                      align="right"
                      sx={{
                        borderTop: "2px solid",
                        borderColor: "primary.main",
                        color: "text.primary",
                        fontWeight: "bold",
                      }}
                    >
                      Grand Total
                    </TableCell>
                    <TableCell
                      sx={{
                        borderTop: "2px solid",
                        borderColor: "primary.main",
                      }}
                    >
                      <TextField
                        fullWidth
                        size="small"
                        value={Number(formData.total_amount || 0).toFixed(2)}
                        disabled
                        InputProps={{
                          startAdornment: (
                            <Typography
                              sx={{
                                mr: 0.5,
                                fontWeight: "bold",
                                color: "primary.main",
                              }}
                            >
                              ₹
                            </Typography>
                          ),
                        }}
                        sx={{
                          "& .MuiInputBase-root": { bgcolor: grandTotalBg },
                          "& .MuiInputBase-input.Mui-disabled": {
                            WebkitTextFillColor: isDark
                              ? theme.palette.primary.light
                              : "#1976d2",
                            fontWeight: 700,
                          },
                        }}
                      />
                    </TableCell>
                    <TableCell
                      sx={{
                        borderTop: "2px solid",
                        borderColor: "primary.main",
                      }}
                    />
                  </TableRow>

                  {/* Final Agreed Row */}
                  <TableRow
                    sx={{
                      position: "sticky",
                      bottom: 0,
                      zIndex: 3,
                      "& td": {
                        bgcolor: "background.paper",
                        borderBottom: "none",
                      },
                    }}
                  >
                    <TableCell colSpan={2}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={isFinalAgreedEditable}
                            onChange={handleFinalAgreedToggle}
                            color="primary"
                            size="small"
                          />
                        }
                        label="Enable Final Agreed Amount"
                        sx={{ color: "text.primary" }}
                      />
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{ fontWeight: "bold", color: "text.primary" }}
                    >
                      Final Agreed Amount
                    </TableCell>
                    <TableCell>
                      <TextField
                        fullWidth
                        size="small"
                        name="final_agreed_amount"
                        type="number"
                        value={formData.final_agreed_amount || 0}
                        onChange={handleChange}
                        disabled={!isFinalAgreedEditable}
                        sx={{
                          "& .MuiInputBase-root": {
                            bgcolor: !isFinalAgreedEditable
                              ? disabledBg
                              : undefined,
                          },
                        }}
                        InputProps={{
                          startAdornment: (
                            <Typography sx={{ mr: 1, color: "text.primary" }}>
                              ₹
                            </Typography>
                          ),
                        }}
                      />
                    </TableCell>
                    <TableCell />
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>

            <Button
              onClick={addItem}
              variant="outlined"
              sx={{ mb: 2, borderRadius: 0 }}
              startIcon={<AddIcon />}
            >
              Add Item
            </Button>
          </>
        )}

        {/* ── Terms & Conditions ── */}
        <Grid item xs={12}>
          <Box mt={3}>
            <Typography
              variant={isMobile ? "subtitle1" : "h6"}
              fontWeight="bold"
              mb={2}
              sx={{
                borderBottom: "2px solid",
                borderColor: "primary.main",
                paddingBottom: 1,
                display: "inline-block",
                color: "text.primary",
              }}
            >
              Terms & Conditions
            </Typography>

            <Paper
              elevation={0}
              sx={{
                p: 2,
                backgroundColor: termsPaperBg,
                borderRadius: 2,
                border: "1px solid",
                borderColor: "divider",
                backgroundImage: "none",
              }}
            >
              {formData.terms_and_conditions.map((term, index) => (
                <Grid
                  container
                  spacing={isMobile ? 1 : 2}
                  key={index}
                  alignItems="center"
                  sx={{
                    mb: 1.5,
                    flexWrap: isMobile ? "wrap" : "nowrap",
                  }}
                >
                  {/* Term Key */}
                  <Grid item xs={12} sm={3}>
                    <TextField
                      fullWidth
                      size="small"
                      value={term.key}
                      onChange={(e) =>
                        handleTermChange(index, "key", e.target.value)
                      }
                      placeholder="Term Title"
                      label={isMobile ? "Term" : ""}
                      variant="outlined"
                      sx={{
                        "& .MuiInputBase-root": {
                          bgcolor: term.is_default
                            ? defaultTermBg
                            : "background.paper",
                          borderRadius: 1,
                        },
                      }}
                    />
                  </Grid>

                  {/* Term Value */}
                  <Grid item xs={10} sm={8}>
                    <TextField
                      fullWidth
                      size="small"
                      value={term.value}
                      onChange={(e) =>
                        handleTermChange(index, "value", e.target.value)
                      }
                      placeholder="Enter condition/value"
                      label={isMobile ? "Value" : ""}
                      variant="outlined"
                      multiline={term.key === "Bank Details"}
                      minRows={term.key === "Bank Details" ? 3 : 1}
                      maxRows={term.key === "Bank Details" ? 5 : 1}
                      sx={{
                        "& .MuiInputBase-root": {
                          bgcolor: "background.paper",
                          borderRadius: 1,
                        },
                      }}
                    />
                  </Grid>

                  {/* Delete Term */}
                  <Grid
                    item
                    xs={2}
                    sm={1}
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                    }}
                  >
                    <IconButton
                      onClick={() => removeTerm(index)}
                      color="error"
                      size="small"
                      sx={{
                        backgroundColor: term.is_default
                          ? defaultTermBg
                          : isDark
                            ? alpha(theme.palette.error.main, 0.15)
                            : "#ffebee",
                        "&:hover": {
                          backgroundColor: term.is_default
                            ? defaultTermBg
                            : isDark
                              ? alpha(theme.palette.error.main, 0.28)
                              : "#ffcdd2",
                        },
                      }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Grid>
                </Grid>
              ))}

              <Box
                mt={2}
                display="flex"
                justifyContent={isMobile ? "center" : "flex-start"}
              >
                <Button
                  variant="outlined"
                  startIcon={<AddIcon />}
                  onClick={addTerm}
                  size={isMobile ? "small" : "medium"}
                  sx={{
                    borderRadius: 2,
                    textTransform: "none",
                    fontWeight: 500,
                  }}
                >
                  Add More Terms
                </Button>
              </Box>
            </Paper>
          </Box>
        </Grid>
      </form>
    </Container>
  );
}
